<template>
  <!-- ================================================================
       Dialog Gán khoản thu cho đối tượng (Lớp / Nhóm / Gói)
       Sử dụng: <revenue-scope-detail-dialog ref="detailDialog" @saved="fetchData" />
       Mở:      this.$refs.detailDialog.open({ targetId, targetType, targetName })
       ================================================================ -->
  <dialog-base
    :visible.sync="visible"
    :title="dialogTitle"
    size="md"
    cancel-text="Huỷ"
    confirm-text="Lưu lại"
    :confirm-loading="isSaving"
    :use-panel="false"
    top="6vh"
    @confirm="handleSave"
    @cancel="handleClose"
  >
    <!-- Thông tin đối tượng -->
    <div class="sk-panel mb-4">
      <div class="sk-panel-head">
        <div class="sk-panel-icon sk-panel-icon--success">
          <i class="el-icon-office-building" />
        </div>
        <div>
          <p class="sk-panel-title">{{ targetName || 'Đang tải...' }}</p>
          <p class="sk-panel-subtitle">
            {{ targetTypeLabel }} &mdash; Chọn các khoản thu muốn thêm
          </p>
        </div>
      </div>
    </div>

    <!-- Ghi chú replace-all -->
    <div class="sk-note sk-note--warning mb-3">
      <i class="el-icon-warning-outline" />
      <span>
        Thao tác lưu sẽ <strong>đồng bộ toàn bộ</strong> danh sách khoản thu đã chọn.<br/>
        Khoản thu bị bỏ tích sẽ bị <strong>xóa hẳn</strong> khỏi lớp/nhóm/gói này và đồng thời cũng bị xoá hẳn khỏi danh sách đăng ký của học sinh trong lớp/nhóm/gói.
      </span>
    </div>

    <!-- Thanh tìm kiếm -->
    <div class="flex items-center gap-3 mb-3">
      <el-input
        v-model="keyword"
        placeholder="Tìm kiếm tên khoản thu..."
        prefix-icon="el-icon-search"
        clearable
        style="max-width: 320px"
        @input="filterRevenues"
      />
      <span class="rsd-count-label">
        Đã chọn:
        <strong class="rsd-count-selected">{{ selectedIds.length }}</strong>
        / {{ revenueList.length }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" v-loading="true" class="rsd-loading-area" />

    <!-- Danh sách checkbox khoản thu -->
    <div v-else class="rsd-checkbox-list">
      <div
        v-for="item in filteredList"
        :key="item.totalRevenueId"
        class="rsd-checkbox-item"
        :class="{ 'rsd-checkbox-item--checked': isChecked(item.totalRevenueId) }"
        @click="toggleItem(item.totalRevenueId)"
      >
        <el-checkbox
          :value="isChecked(item.totalRevenueId)"
          class="rsd-checkbox"
          @change="toggleItem(item.totalRevenueId)"
          @click.native.stop
        />
        <el-tooltip
          v-if="item.code"
          :content="`Mã khoản: ${item.code}`"
          placement="top"
        >
          <span class="rsd-checkbox-label">{{ item.totalRevenueName }}</span>
        </el-tooltip>
        <span v-else class="rsd-checkbox-label">{{ item.totalRevenueName }}</span>
      </div>

      <!-- Trạng thái rỗng -->
      <div v-if="filteredList.length === 0" class="rsd-empty">
        <i class="el-icon-document-remove rsd-empty-icon" />
        <p v-if="keyword">Không tìm thấy khoản thu phù hợp.</p>
        <p v-else>Trường chưa có khoản thu nào.</p>
      </div>
    </div>
  </dialog-base>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import RevenueScopeService from "@/services/tutition_v3/RevenueScopeService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

const TARGET_TYPE_LABELS = {
  CLASS: "Lớp học",
  GROUP: "Nhóm học sinh",
  PACKAGE: "Gói dịch vụ",
};

export default {
  name: "RevenueScopeDetailDialog",
  components: { DialogBase },

  data() {
    return {
      visible: false,
      isLoading: false,
      isSaving: false,
      targetId: null,
      targetType: "",
      targetName: "",
      revenueList: [],
      filteredList: [],
      selectedIds: [],
      keyword: "",
    };
  },

  computed: {
    dialogTitle() {
      return "Thêm khoản thu";
    },
    targetTypeLabel() {
      return TARGET_TYPE_LABELS[this.targetType] || this.targetType || "";
    },
  },

  methods: {
    // ─── Public API ────────────────────────────────────────────────
    /**
     * Mở dialog và load danh sách khoản thu cho đối tượng
     * @param {Object} options - { targetId, targetType, targetName }
     */
    open({ targetId, targetType, targetName }) {
      this.targetId = targetId;
      this.targetType = targetType;
      this.targetName = targetName || "";
      this.keyword = "";
      this.revenueList = [];
      this.filteredList = [];
      this.selectedIds = [];
      this.visible = true;
      this.fetchDetails();
    },

    // ─── API ───────────────────────────────────────────────────────
    async fetchDetails() {
      this.isLoading = true;
      try {
        const res = await RevenueScopeService.getDetails({
          targetId: this.targetId,
          targetType: this.targetType,
        });
        const data = res.data && res.data.data;
        this.revenueList = Array.isArray(data) ? data : [];
        // Pre-tick các khoản thu đã gán (registered = true)
        this.selectedIds = this.revenueList
          .filter((item) => item.registered)
          .map((item) => item.totalRevenueId);
        this.filterRevenues();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Không thể tải danh sách khoản thu!"
        );
      } finally {
        this.isLoading = false;
      }
    },

    async handleSave() {
      this.isSaving = true;
      try {
        const res = await RevenueScopeService.register({
          targetId: this.targetId,
          targetType: this.targetType,
          idTotalRevenues: [...this.selectedIds],
        });
        if (res.data && res.data.status === 200) {
          NotifyMessage.leftCornerNotificationSuccess(
            (res.data && res.data.message) || "Lưu khoản thu thành công!"
          );
          this.$emit("saved");
          this.handleClose();
        }
      } catch (err) {
        const status = err && err.response && err.response.status;
        const message =
          err && err.response && err.response.data && err.response.data.message;
        if (status === 404 || status === 400) {
          NotifyMessage.leftCornerNotificationError(message || "Dữ liệu không hợp lệ!");
        } else {
          NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra, vui lòng thử lại!");
        }
      } finally {
        this.isSaving = false;
      }
    },

    // ─── Helpers ───────────────────────────────────────────────────
    isChecked(id) {
      return this.selectedIds.indexOf(id) !== -1;
    },

    toggleItem(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(idx, 1);
      }
    },

    filterRevenues() {
      const kw = (this.keyword || "").toLowerCase().trim();
      this.filteredList = kw
        ? this.revenueList.filter(
            (item) =>
              (item.totalRevenueName || "").toLowerCase().includes(kw) ||
              (item.code || "").toLowerCase().includes(kw)
          )
        : [...this.revenueList];
    },

    handleClose() {
      this.visible = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";

/* ─── Count label ──────────────────────────────────────── */
.rsd-count-label {
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}
.rsd-count-selected {
  color: #2563eb;
  font-weight: 700;
}

/* ─── Loading area ─────────────────────────────────────── */
.rsd-loading-area {
  height: 160px;
}

/* ─── Checkbox list ────────────────────────────────────── */
.rsd-checkbox-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 6px 0;
  background: #f8fafc;
}

.rsd-checkbox-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #eff6ff;
  }

  &--checked {
    background: #eff6ff;

    .rsd-checkbox-label {
      color: #2563eb;
      font-weight: 600;
    }
  }
}

.rsd-checkbox {
  flex-shrink: 0;
  pointer-events: none;
}

.rsd-checkbox-label {
  font-size: 14px;
  color: #1e293b;
  line-height: 1.5;
  user-select: none;
  transition: color 0.15s;
}

/* ─── Empty state ──────────────────────────────────────── */
.rsd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  color: #94a3b8;
  gap: 10px;
  font-size: 14px;
  text-align: center;
}

.rsd-empty-icon {
  font-size: 40px;
}

/* ─── Override Element UI ──────────────────────────────── */
/deep/ .el-input__inner {
  border-radius: 10px;
}

/deep/ .el-button {
  border-radius: 10px;
}

/* ─── Responsive ───────────────────────────────────────── */
@media (max-width: 768px) {
  .rsd-checkbox-list {
    max-height: 260px;
  }
}
</style>
